import { DARK_BACKGROUND_LUMINANCE_THRESHOLD, LUMINANCE_SAMPLE_GRID } from './constants';

/** Minimal ImageData-compatible shape so these helpers stay unit-testable. */
export interface RawImage {
  width: number;
  height: number;
  data: Uint8ClampedArray | number[];
}

export function luminance(r: number, g: number, b: number): number {
  return 0.299 * r + 0.587 * g + 0.114 * b;
}

/** Flattens any alpha onto an opaque white background, in place. */
export function compositeOnWhite(img: RawImage): void {
  const { data } = img;
  for (let i = 0; i < data.length; i += 4) {
    const a = data[i + 3] / 255;
    if (a === 1) continue;
    data[i] = Math.round(data[i] * a + 255 * (1 - a));
    data[i + 1] = Math.round(data[i + 1] * a + 255 * (1 - a));
    data[i + 2] = Math.round(data[i + 2] * a + 255 * (1 - a));
    data[i + 3] = 255;
  }
}

/**
 * Median luminance over an evenly spaced sampling grid (all pixels when the
 * image is smaller than the grid).
 */
export function medianLuminance(img: RawImage, grid = LUMINANCE_SAMPLE_GRID): number {
  const { width, height, data } = img;
  if (!width || !height) return 255;
  const cols = Math.min(width, grid);
  const rows = Math.min(height, grid);
  const samples: number[] = [];
  for (let r = 0; r < rows; r += 1) {
    const y = rows === 1 ? 0 : Math.round((r * (height - 1)) / (rows - 1));
    for (let c = 0; c < cols; c += 1) {
      const x = cols === 1 ? 0 : Math.round((c * (width - 1)) / (cols - 1));
      const i = (y * width + x) * 4;
      samples.push(luminance(data[i], data[i + 1], data[i + 2]));
    }
  }
  if (!samples.length) return 255;
  samples.sort((a, b) => a - b);
  const mid = samples.length >> 1;
  return samples.length % 2 ? samples[mid] : (samples[mid - 1] + samples[mid]) / 2;
}

/** newValue = 255 - oldValue on RGB, alpha untouched. */
export function invertRgb(img: RawImage): void {
  const { data } = img;
  for (let i = 0; i < data.length; i += 4) {
    data[i] = 255 - data[i];
    data[i + 1] = 255 - data[i + 1];
    data[i + 2] = 255 - data[i + 2];
  }
}

/**
 * Mild grayscale + contrast stretch. Deliberately gentle: aggressive
 * sharpening/thresholding destroys thin Devanagari strokes and matras.
 */
export function grayscaleContrast(img: RawImage, contrast = 1.3): void {
  const { data } = img;
  for (let i = 0; i < data.length; i += 4) {
    const g = luminance(data[i], data[i + 1], data[i + 2]);
    let v = (g - 128) * contrast + 128;
    if (v < 0) v = 0;
    else if (v > 255) v = 255;
    const out = Math.round(v);
    data[i] = out;
    data[i + 1] = out;
    data[i + 2] = out;
  }
}

export function toGrayscale(img: RawImage): Float64Array {
  const { data } = img;
  const gray = new Float64Array(data.length / 4);
  for (let i = 0, p = 0; i < data.length; i += 4, p += 1) {
    gray[p] = luminance(data[i], data[i + 1], data[i + 2]);
  }
  return gray;
}

/**
 * Local-mean (Bradley/Sauvola-style) adaptive threshold. Produces a clean
 * bi-level canvas for posters with uneven lighting or coloured backgrounds.
 * Window size scales with the image so Devanagari matras survive.
 */
export function adaptiveThreshold(img: RawImage, windowRatio = 0.06, biasPercent = 12): void {
  const { width, height, data } = img;
  if (!width || !height) return;
  const gray = toGrayscale(img);

  // Integral image for O(1) window means.
  const integral = new Float64Array((width + 1) * (height + 1));
  for (let y = 0; y < height; y += 1) {
    let rowSum = 0;
    for (let x = 0; x < width; x += 1) {
      rowSum += gray[y * width + x];
      integral[(y + 1) * (width + 1) + (x + 1)] = integral[y * (width + 1) + (x + 1)] + rowSum;
    }
  }

  const radius = Math.max(6, Math.round(Math.min(width, height) * windowRatio));
  const bias = (100 - biasPercent) / 100;

  for (let y = 0; y < height; y += 1) {
    const y0 = Math.max(0, y - radius);
    const y1 = Math.min(height - 1, y + radius);
    for (let x = 0; x < width; x += 1) {
      const x0 = Math.max(0, x - radius);
      const x1 = Math.min(width - 1, x + radius);
      const area = (x1 - x0 + 1) * (y1 - y0 + 1);
      const sum =
        integral[(y1 + 1) * (width + 1) + (x1 + 1)] -
        integral[y0 * (width + 1) + (x1 + 1)] -
        integral[(y1 + 1) * (width + 1) + x0] +
        integral[y0 * (width + 1) + x0];
      const mean = sum / area;
      const value = gray[y * width + x] < mean * bias ? 0 : 255;
      const i = (y * width + x) * 4;
      data[i] = value;
      data[i + 1] = value;
      data[i + 2] = value;
      data[i + 3] = 255;
    }
  }
}

/** Non-destructive preprocessing variants tried for hard images. */
export type VariantId = 'upscaled' | 'grayscale-contrast' | 'inverted-grayscale' | 'adaptive-threshold';

export const POSTER_VARIANTS: VariantId[] = [
  'upscaled',
  'grayscale-contrast',
  'inverted-grayscale',
  'adaptive-threshold',
];

export interface PrepMeta {
  medianLuminance: number;
  inverted: boolean;
  variant: VariantId;
}

export interface ProcessOptions {
  variant?: VariantId;
  darkThreshold?: number;
  contrast?: number;
}

/**
 * Single ordered pipeline used for every OCR canvas:
 * composite → dark-background detection → at most one inversion → variant
 * specific enhancement. Never applied to the displayed preview.
 */
export function processForOcr(img: RawImage, options: ProcessOptions = {}): PrepMeta {
  const {
    variant = 'upscaled',
    darkThreshold = DARK_BACKGROUND_LUMINANCE_THRESHOLD,
    contrast = 1.3,
  } = options;

  compositeOnWhite(img);
  const median = medianLuminance(img);
  const autoInvert = median < darkThreshold;
  // Variant 3 always produces one inversion relative to the source; every other
  // variant inverts only when the canvas really is a dark background. In all
  // cases the inversion happens exactly once.
  const shouldInvert = variant === 'inverted-grayscale' ? true : autoInvert;

  if (shouldInvert) invertRgb(img);
  if (variant === 'grayscale-contrast' || variant === 'inverted-grayscale') {
    grayscaleContrast(img, contrast);
  } else if (variant === 'adaptive-threshold') {
    adaptiveThreshold(img);
  }

  return { medianLuminance: median, inverted: shouldInvert, variant };
}
